import { Box, Card, Chip, Dialog, Button as MUIButton } from '@mui/material';
import Stack from '@mui/material/Stack';
import { ReactElement, useState } from 'react';
import {
  ArrayField,
  BooleanField,
  BulkDeleteButton,
  Button,
  ChipField,
  CreateButton,
  Datagrid,
  DeleteButton,
  EditButton,
  Filter,
  FunctionField,
  List,
  SelectField,
  SingleFieldList,
  TextField,
  TopToolbar,
  useListController,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionButton } from 'src/routes/components';
import EditCategory from './Edit';
import CreateCategory from './Create';
import { chains } from 'src/assets/data';
import AuditLogDialog from 'src/pages/admin/components/AuditLog/AuditLogDialog';
import { Iconify } from 'src/components/iconify';
import { XButton } from 'src/pages/admin/components/XButton';

const ListFilter = (props: any) => {
  const { filterValues } = useListController();

  return (
    <Filter {...(props || {})}>
      {/* <FilterGroup alwaysOn spacing={0} p={0}>
        <FilterGroup>
          <XTextInput source="name" label="module.categories.name" />
        </FilterGroup>
      </FilterGroup> */}
    </Filter>
  );
};

const View = () => {
  const [popup, setPopup] = useState<ReactElement>();
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditTitle, setAuditTitle] = useState('Audit Logs - Categories');
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const translate = useTranslate();

  return (
    <PermissionGuard resource="categories" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('resource.category')}
          links={[
            { name: translate('resource.home'), href: '#' },
            { name: translate('resource.category'), href: `/${Resources.categories}` },
            { name: translate('resource.list') },
          ]}
          action={
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
              mb={1}
            >
              <PermissionGuard resource="categories" action="create" fallback={<></>}>
                <CreateButton
                  variant="contained"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setPopup(<CreateCategory onClose={() => setPopup(undefined)} />);
                  }}
                />
              </PermissionGuard>
              <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                <Button
                  variant="contained"
                  size="medium"
                  color="secondary"
                  onClick={() => {
                    setSelectedRecord({});
                    setAuditTitle(`${translate('resource.auditLogs')} - ${translate('resource.category')}`);
                    setAuditLogOpen(true);
                  }}
                  label={translate('resource.auditLogs')}
                >
                  <Iconify icon="ph:note" />
                </Button>
              </PermissionGuard>
            </Stack>
          }
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List filters={<ListFilter />} perPage={10} exporter={false}>
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={
                  <PermissionButton resource="categories" action="delete">
                    <BulkDeleteButton />
                  </PermissionButton>
                }
                sx={{
                  '.RaBulkActionsToolbar-toolbar': {
                    position: 'relative !important',
                    transform: 'none !important',
                  },
                }}
              >
                <TextField source="name" label="module.categories.name" sortable={false} />

                <SelectField
                  source="chainId"
                  choices={chains}
                  label="module.categories.chain"
                  sortable={false}
                />

                <ArrayField source="keywords" sortable={false}>
                  <SingleFieldList linkType={false}>
                    <FunctionField
                      render={(item: any) => (
                        <ChipField
                          variant="outlined"
                          color="default"
                          record={{ name: item }}
                          source="name"
                          size="small"
                        />
                      )}
                    />
                  </SingleFieldList>
                </ArrayField>

                <FunctionField
                  label="module.categories.enable"
                  render={(record) => (
                    <Chip
                      label={record.isEnable ? 'True' : 'False'}
                      color={record.isEnable ? 'success' : 'error'}
                      size="small"
                    />
                  )}
                  sortable={false}
                />

                <FunctionField
                  render={(record: any) => (
                    <Stack direction="row" spacing={2}>
                      <PermissionGuard resource="categories" action="update" fallback={<></>}>
                        <EditButton
                          onClick={(e) => {
                            e.preventDefault();
                            setPopup(
                              <EditCategory id={record.id} onClose={() => setPopup(undefined)} />
                            );
                          }}
                        />
                      </PermissionGuard>
                      <PermissionGuard resource="categories" action="delete" fallback={<></>}>
                        <DeleteButton mutationMode="pessimistic" />
                      </PermissionGuard>
                      <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                        <XButton
                          variant="text"
                          size="small"
                          color="secondary"
                          disabled={!record.hasAuditLog}
                          onClick={() => {
                            setAuditTitle(
                              `Audit Logs - Name: ${record.name} - Chain: ${record.chainId} (ID: ${record.id}) `
                            );
                            setSelectedRecord(record);
                            setAuditLogOpen(true);
                          }}
                          label={translate('resource.auditLogs')}
                        >
                          <Iconify icon="ph:note" />
                        </XButton>
                      </PermissionGuard>
                    </Stack>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
        <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="sm" fullWidth>
          {popup}
        </Dialog>
        <AuditLogDialog
          open={auditLogOpen}
          onClose={() => setAuditLogOpen(false)}
          resource={Resources.categories}
          recordId={selectedRecord?.id}
          title={auditTitle}
        />
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
